import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";

import ReadOnlyProfile from "./ReadOnlyProfile";
import ReadOnlyPitch from "./ReadOnlyPitch";
import ReadOnlyClaims from "./ReadOnlyClaims";
import ReadOnlyRecords from "./ReadOnlyRecords";
import EvaluationPanel from "./EvaluationPanel";

function StartupView() {
  const { founderId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // 🔑 Determine mode
  const mode = searchParams.get("mode") || "view";
  const isReviewMode = mode === "review";

  /**
   * 🔑 Shared evaluation context
   * This is populated by EvaluationPanel
   * and passed down to ReadOnlyClaims
   */
  const [evaluationContext, setEvaluationContext] = useState({
    evaluationId: null,
    status: null,
  });

  return (
    <div>
      {/* ================= HEADER ================= */}
      <div style={{ marginBottom: 24 }}>
        <button
          className="btn-secondary"
          onClick={() => navigate(-1)}
          title="Return to your previous list of startups."
        >
          ← Back
        </button>

        <h1
          style={{ marginTop: 16 }}
          title={
            isReviewMode
              ? "You are in review mode. Your assessment is private until you submit it."
              : "You are viewing a shared startup profile. No evaluation actions are available."
          }
        >
          {isReviewMode ? "Startup Evaluation" : "Startup Overview"}
        </h1>

        <p
          style={{ color: "#64748b", maxWidth: 900 }}
          title="Prufyy evaluations are advisory. You are not certifying accuracy or truth."
        >
          {isReviewMode ? (
            <>
              You are reviewing this startup.
              <br />
              <strong>Your evaluation is editable and private</strong> until you
              explicitly submit it. Founders cannot see drafts.
            </>
          ) : (
            <>
              You are viewing a shared startup profile.
              <br />
              Editing and evaluation actions are disabled in this mode.
            </>
          )}
        </p>
      </div>

      {/* ================= RESPONSIBILITY FRAMING ================= */}
      {isReviewMode && (
        <div
          className="card"
          style={{
            padding: 16,
            marginBottom: 24,
            background: "linear-gradient(135deg,#f8fafc,#eef2f7)",
          }}
          title="This reminder helps align expectations for both evaluators and founders."
        >
          <strong>Evaluator responsibility</strong>
          <p style={{ marginTop: 6, fontSize: 14, color: "#475569" }}>
            Your role is to assess <strong>clarity, evidence, and reasoning</strong>.
            <br />
            You are not verifying facts or certifying outcomes.
            <br />
            Your feedback contributes perspective — not judgment.
          </p>
        </div>
      )}

      {/* ================= EVALUATION PANEL ================= */}
      <div
        title={
          isReviewMode
            ? "This panel controls your evaluation draft and submission."
            : "Evaluation controls are hidden in view-only mode."
        }
      >
        <EvaluationPanel
          founderId={founderId}
          mode={mode}
          onEvaluationLoaded={(ctx) => setEvaluationContext(ctx)}
        />
      </div>

      {/* ================= READ-ONLY SECTIONS ================= */}
      <div
        title="Founder profile information provided for context."
      >
        <ReadOnlyProfile founderId={founderId} />
      </div>

      <div
        title="This is the pitch deck the founder has shared for evaluation."
      >
        <ReadOnlyPitch founderId={founderId} />
      </div>

      {/* ================= CLAIMS ================= */}
      <div
        title="Review claims alongside their evidence. Focus on support and clarity."
      >
        <ReadOnlyClaims
          founderId={founderId}
          evaluation={evaluationContext}
          mode={mode}
        />
      </div>

      <div
        title="Additional records supplied by the founder for reference."
      >
        <ReadOnlyRecords founderId={founderId} />
      </div>
    </div>
  );
}

export default StartupView;
